import React, { useState, useEffect } from "react";
import postsStyles from "./personalposts.module.css";
import { HiOutlineLightBulb } from "react-icons/hi";
import { FaRegPaperPlane } from "react-icons/fa";
import { CiMenuKebab } from "react-icons/ci";
import { FiShare2 } from "react-icons/fi";
import AdminDropdown from "../../../src/components/adminDropdown/AdminDropdown";
import defaultImage from "../../appImges/avatartTemplate/default_img.png";
import EditComment from "./EditComment";
import axios from "axios";
import { toast } from "react-toastify";
import ReactPlayer from "react-player";
import { format } from "date-fns";
import ModalComp from "../../../src/components/popUp/ModalComp";
import { useUser } from "../../storage/UserContext"; // context hook

const apiUrl = process.env.REACT_APP_URL;

// receive data from AuthHomePage
const OtherPeoplePosts = ({ userImage, username, role }) => {
  /**
   * This component is used by the AuthHomePage page to display posts that were not published by the active user. The component handles posts and comments display, comment publishing, editing and deletion, and post voting and sharing.
   */

  // State to manage expanded posts for displaying all comments
  const [expandedPosts, setExpandedPosts] = useState({});
  // State to manage posts data
  const [posts, setPosts] = useState([]);
  // State to manage new comments for each post
  const [newComment, setNewComment] = useState({});
  // State to manage dropdowns for each comment with edit and delete options
  const [dropdowns, setDropdowns] = useState({});
  // State to manage the edit comment dialog visibility and data
  const [editCommentData, setEditCommentData] = useState({
    isOpen: false,
    commentId: null,
    postId: null,
    content: "",
  });

  const [offset, setOffset] = useState(0);
  const qty = 10;
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [isModalOpen, setModalOpen] = useState(false);

  const [postToDelete, setPostToDelete] = useState(null); // Post to be deleted
  function formatDate(dateString) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  // Fetch posts data
  const fetchPostsData = async () => {
    if (!username) return; // Ensure the username is available before making the API call

    try {
      const postsRes = await axios.get(
        `${apiUrl}/feed/${username}?qty=${qty}&offset=${offset}`,
        { withCredentials: true }
      );

      if (postsRes.status === 200) {
        const newPosts = postsRes.data.posts;
        setPosts((prevPosts) => [...prevPosts, ...newPosts]);

        if (newPosts.length < qty) {
          setHasMorePosts(false);
        }
      }
    } catch (e) {
      console.error("Failed to fetch posts data", e);
    }
  };

  useEffect(() => {
    if (username) {
      fetchPostsData();
    }
  }, [offset, username]);

  const handleSeeMore = () => {
    setOffset((prevOffset) => prevOffset + qty);
  };

  /**
   * Toggles the visibility of comments for a specific post.
   *
   * @param {string} postId - The ID of the post whose comments' visibility needs to be toggled.
   */
  const toggleComments = (postId) => {
    // Update the state for expandedPosts
    setExpandedPosts((prev) => ({
      // Spread the previous state to retain other posts' visibility status
      ...prev,
      // Toggle the visibility status for the specified postId
      [postId]: !prev[postId],
    }));
  };

  // Toggle the dropdown for a specific comment to show/hide edit and delete options
  const toggleDropdown = (commentId) => {
    setDropdowns((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  // Handle the addition of a new comment
  const handleNewComment = async (postId) => {
    try {
      if (!newComment[postId]) {
        return;
      }

      const response = await axios.post(
        `${apiUrl}/comments/create`,
        { postId, content: newComment[postId] },
        { withCredentials: true }
      );

      if (response.status === 200) {
        const newCommentId = response.data.commentId; // Get the new comment ID from the response

        const updatedPosts = posts.map((post) =>
          post.post_id === postId
            ? {
                ...post,
                details: {
                  ...post.details,
                  comments: [
                    ...post.details.comments,
                    {
                      comment_id: newCommentId, // Add the new comment ID
                      content: newComment[postId],
                      commenter: username,
                      commenter_img: userImage || defaultImage,
                      created_at: new Date().toISOString(),
                      updated_at: new Date().toISOString(),
                    },
                  ],
                },
              }
            : post
        );
        setPosts(updatedPosts);
        setNewComment((prev) => ({ ...prev, [postId]: "" }));
      }
    } catch (e) {
      console.error("Failed to add new comment", e);
    }
  };
  // Function to open the modal and set the post to be deleted
  const openDeleteModal = (postId) => {
    setPostToDelete(postId);
    setModalOpen(true);
  };

  // Function to handle the actual delete action
  const handleDeletePost = async () => {
    if (!postToDelete) return;

    try {
      const response = await axios.put(
        `${apiUrl}/posts/deactivate/${postToDelete}`,
        {},
        { withCredentials: true }
      );

      if (response.status === 200) {
        const updatedPosts = posts.filter(
          (post) => post.post_id !== postToDelete
        );
        setPosts(updatedPosts);
      } else {
        console.error("Failed to deactivate the post:", response.statusText);
      }
    } catch (error) {
      console.error("Error deleting post:", error);
    } finally {
      setModalOpen(false); // Close the modal
      setPostToDelete(null); // Clear the post to delete state
    }
  };

  // Add a new like or delete a like from a post according to the current state of the user's like
  const addDeleteVote = async (postId) => {
    try {
      const response = await axios.post(
        `${apiUrl}/posts/like`,
        { post_id: postId, username: username },
        { withCredentials: true }
      );

      if (response.status === 200) {
        const updatedPosts = posts.map((post) =>
          post.post_id === postId
            ? { ...post, votes: response.data.votes }
            : post
        );
        setPosts(updatedPosts);
      }
    } catch (error) {
      console.error("Error updating vote:", error);
    }
  };

  const handleDeleteComment = async (commentId, postId) => {
    try {
      const response = await axios.put(
        `${apiUrl}/comments/deactivate/${commentId}`,
        {},
        { withCredentials: true }
      );

      if (response.status === 200) {
        // Filter out the deactivated comment from the post's comments
        const updatedPosts = posts.map((post) => {
          if (post.post_id === postId) {
            return {
              ...post,
              details: {
                ...post.details,
                comments: post.details.comments.filter(
                  (comment) => comment.comment_id !== commentId
                ),
              },
            };
          }
          return post;
        });
        setPosts(updatedPosts);

        // Close the dropdown after deleting the comment
        setDropdowns((prev) => ({
          ...prev,
          [commentId]: false,
        }));
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  // Open the edit comment dialog with the comment data
  const handleEditComment = (commentId, postId, content) => {
    // Close the dropdown
    setDropdowns((prev) => ({
      ...prev,
      [commentId]: false,
    }));
    //and after this open the edit comment dialog
    setEditCommentData({
      isOpen: true,
      commentId,
      postId,
      content,
    });
  };

  // Close the edit comment dialog
  const handleCloseEditComment = () => {
    setEditCommentData({
      isOpen: false,
      commentId: null,
      postId: null,
      content: "",
    });
  };

  // Update the content of the comment being edited
  const handleConfirmEditComment = async () => {
    try {
      const { commentId, postId, content } = editCommentData;

      if (!content) {
        return;
      }

      const response = await axios.put(
        `${apiUrl}/comments/update/${commentId}`,
        { content },
        { withCredentials: true }
      );

      if (response.status === 200) {
        const updatedPosts = posts.map((post) => {
          if (post.post_id === postId) {
            return {
              ...post,
              details: {
                ...post.details,
                comments: post.details.comments.map((comment) =>
                  comment.comment_id === commentId
                    ? {
                        ...comment,
                        content,
                        updated_at: new Date().toISOString(),
                      }
                    : comment
                ),
              },
            };
          }
          return post;
        });
        setPosts(updatedPosts);
        handleCloseEditComment();
      }
    } catch (error) {
      console.error("Error editing comment:", error);
    }
  };

  // Share a post
  const handleShare = async (postId) => {
    try {
      const response = await axios.post(
        `${apiUrl}/posts/share`,
        { postId: postId, username: username },
        { withCredentials: true }
      );

      if (response.status === 200) {
        toast.success("Post shared successfully");
      }
    } catch (error) {
      if (error.response && error.response.status === 409) {
        toast.warning("Post has already been shared");
      } else {
        console.error("Error sharing post:", error);
        toast.error("Error sharing post");
      }
    }
  };

  const visitProfile = (username) => {
    window.open(`/profile/${username}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className={postsStyles.mainPostsStyle}>
      {posts.map((post) => {
        let mainPostUser = post.username;
        let mainPostImg = post.details.publisher_img;

        if (post.sharing_username) {
          mainPostUser = post.sharing_username;
          mainPostImg = post.details.sharer_img;
        }

        return (
          <div
            key={post.post_id}
            className={`${postsStyles.postStyle} ${
              post.sharing_username ? postsStyles.sharedPost : ""
            }`}
          >
            <div className={postsStyles.headerPost}>
              <div>
                <figure
                  className={
                    postsStyles.avatar + " " + postsStyles.linkedAvatar
                  }
                  onClick={() => visitProfile(mainPostUser)}
                >
                  <img
                    src={
                      mainPostImg && mainPostImg !== "default_img_link"
                        ? mainPostImg
                        : defaultImage
                    }
                    alt={mainPostUser}
                    onError={(e) => {
                      e.target.src = defaultImage;
                      e.target.onerror = null; // Prevents infinite loop if the default image is also not found
                    }}
                  />
                  <figcaption>{mainPostUser}</figcaption>
                </figure>
                <p className={postsStyles.postDate}>
                  <span>{format(new Date(post.created_at), "dd/MM/yyyy")}</span>
                  <span>{new Date(post.created_at).toLocaleTimeString()}</span>
                </p>
              </div>
              {post.viewing_permission === 1 && (
                <>
                  {role === "Admin" ? (
                    // Show dropdown menu for admin
                    <AdminDropdown
                      openDeleteModal={openDeleteModal}
                      handleDelete={handleDeletePost}
                      handleShare={handleShare}
                      postId={post.post_id}
                    />
                  ) : (
                    // Show share option for non-admin users
                    <p
                      className={postsStyles.sharePost}
                      onClick={() => handleShare(post.post_id)}
                    >
                      <FiShare2 />
                    </p>
                  )}
                </>
              )}
            </div>
            <div className={postsStyles.postWrapper}>
              {post.sharing_username && (
                <div
                  className={postsStyles.sharedPostInfo}
                  onClick={() => visitProfile(post.username)}
                >
                  <span>Posted by: {post.username} </span>
                  <img
                    className={postsStyles.sharedPostAuthorImg}
                    src={
                      post.details.publisher_img &&
                      post.details.publisher_img !== "default_img_link"
                        ? post.details.publisher_img
                        : defaultImage
                    }
                    alt={post.username}
                    onError={(e) => {
                      e.target.src = defaultImage;
                      e.target.onerror = null; // Prevents infinite loop if the default image is also not found
                    }}
                  />
                </div>
              )}
              <section className={postsStyles.postData}>{post.content}</section>
            </div>
            {/* {post.details.post_img && (
            <figure className={postsStyles.postPic}>
              <img src={post.details.post_img} alt="post pic" />
            </figure>
          )} */}
            {post?.img_link && (
              <figure className={postsStyles.postPic}>
                <img src={post.img_link} alt="post pic" />
              </figure>
            )}
            {post?.video_link && (
              <div className={postsStyles.postVideo}>
                <ReactPlayer
                  url={post.video_link}
                  controls={true}
                  width="100%"
                  height="130%"
                />
              </div>
            )}
            <div className={postsStyles.likes}>
              <span
                className={postsStyles.spanBulb}
                onClick={() => addDeleteVote(post.post_id)}
              >
                <HiOutlineLightBulb />
                {post.votes}
              </span>
            </div>

            <div className={postsStyles.comments}>
              {post.comment_permission === 1 && (
                <div className={postsStyles.newCommentContainer}>
                  <textarea
                    onChange={(e) =>
                      setNewComment((prev) => ({
                        ...prev,
                        [post.post_id]: e.target.value,
                      }))
                    }
                    placeholder="Add a new comment..."
                    value={newComment[post.post_id] || ""}
                  />
                  <button
                    className={postsStyles.commentSubmitBtn}
                    onClick={() => handleNewComment(post.post_id)}
                  >
                    <FaRegPaperPlane />
                  </button>
                </div>
              )}

              {/* Posts comment area  */}
              {post.details?.comments?.length > 0 && (
                <div>
                  {/* Check if the comments for the post are expanded */}
                  {expandedPosts[post.post_id]
                    ? // If comments are expanded, display all comments
                      post.details.comments.map((comment) => (
                        <div
                          key={comment.comment_id}
                          className={postsStyles.singleComment}
                        >
                          <figure className={postsStyles.commentAuthorInfo}>
                            <img
                              src={
                                comment.commenter_img
                                  ? comment.commenter_img
                                  : defaultImage
                              }
                              alt={comment.commenter}
                              onError={(e) => {
                                e.target.src = defaultImage;
                                e.target.onerror = null; // Prevents infinite loop if the default image is also not found
                              }}
                            />
                            <figcaption>
                              <p>{comment.commenter}</p>
                              <p>{formatDate(comment.updated_at)}</p>
                            </figcaption>
                          </figure>

                          <div className={postsStyles.commentData}>
                            <p>{comment.content}</p>
                          </div>
                          {/* Display delete icon if the current user is the commenter or an admin */}
                          {(role === "Admin" ||
                            comment.commenter === username ||
                            post.creator === username) && (
                            <div className={postsStyles.dropdownContainer}>
                              <CiMenuKebab
                                className={postsStyles.dropdownTrigger}
                                onClick={() =>
                                  toggleDropdown(comment.comment_id)
                                }
                              />
                              {dropdowns[comment.comment_id] && (
                                <div className={postsStyles.dropdownMenu}>
                                  {/* Admin can delete any comment, user can delete their own */}
                                  {(role === "Admin" ||
                                    comment.commenter === username) && (
                                    <p
                                      onClick={() =>
                                        handleDeleteComment(
                                          comment.comment_id,
                                          post.post_id
                                        )
                                      }
                                    >
                                      Delete
                                    </p>
                                  )}

                                  {/* Commenter can edit their own comment */}
                                  {comment.commenter === username && (
                                    <p
                                      onClick={() =>
                                        handleEditComment(
                                          comment.comment_id,
                                          post.post_id,
                                          comment.content
                                        )
                                      }
                                    >
                                      Edit
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    : post.details.comments.slice(-1).map((comment) => (
                        <div
                          key={comment.comment_id}
                          className={postsStyles.singleComment}
                        >
                          <figure className={postsStyles.commentAuthorInfo}>
                            <img
                              src={
                                comment.commenter_img
                                  ? comment.commenter_img
                                  : defaultImage
                              }
                              alt={comment.commenter}
                              onError={(e) => {
                                e.target.src = defaultImage;
                                e.target.onerror = null; // Prevents infinite loop if the default image is also not found
                              }}
                            />
                            <figcaption>
                              <p>{comment.commenter}</p>
                              <p className={postsStyles.commentDate}>
                                {formatDate(comment.updated_at)}
                              </p>
                            </figcaption>
                          </figure>

                          <div className={postsStyles.commentData}>
                            <p>{comment.content}</p>
                          </div>
                          {/* Display delete icon if the current user is the commenter or an admin */}
                          {(comment.commenter === username ||
                            role === "Admin") && (
                            <div className={postsStyles.dropdownContainer}>
                              <CiMenuKebab
                                className={postsStyles.dropdownTrigger}
                                onClick={() =>
                                  toggleDropdown(comment.comment_id)
                                }
                              />
                              {dropdowns[comment.comment_id] && (
                                <div className={postsStyles.dropdownMenu}>
                                  {/* Show Edit option only if the current user is the commenter */}
                                  {comment.commenter === username && (
                                    <p
                                      onClick={() =>
                                        handleEditComment(
                                          comment.comment_id,
                                          post.post_id,
                                          comment.content
                                        )
                                      }
                                    >
                                      Edit
                                    </p>
                                  )}
                                  {/* Show Delete option for both the commenter and the admin */}
                                  <p
                                    onClick={() =>
                                      handleDeleteComment(
                                        comment.comment_id,
                                        post.post_id
                                      )
                                    }
                                  >
                                    Delete
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                  <p
                    className={postsStyles.toggleCommentsBtn}
                    // Toggle comments visibility
                    onClick={() => toggleComments(post.post_id)}
                  >
                    {expandedPosts[post.post_id] ? "Hide all" : "See more"}
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      })}
      <EditComment
        isOpen={editCommentData.isOpen}
        onClose={handleCloseEditComment}
        onConfirm={handleConfirmEditComment}
        commentContent={editCommentData.content}
        onChangeContent={(content) =>
          setEditCommentData((prev) => ({ ...prev, content }))
        }
      />
      {hasMorePosts && (
        <button onClick={handleSeeMore} className={postsStyles.seeMoreBtn}>
          See More
        </button>
      )}

      {/* Modal for delete confirmation */}
      <ModalComp
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)} // Close modal without deleting
      >
        <p className={postsStyles.deleteConfirmationPost}>
          Are you sure you want to delete this post?
        </p>
        <button
          className={postsStyles.button}
          onClick={() => handleDeletePost(postToDelete)}
        >
          Yes, Delete
        </button>{" "}
        <button
          className={postsStyles.button}
          onClick={() => setModalOpen(false)}
        >
          Cancel
        </button>
      </ModalComp>
    </div>
  );
};

export default OtherPeoplePosts;
